// GTM dataLayer tracking API for the Sunscreen/Moisturiser "Pair With" exit-intent popup.
// The popup UI itself is built/injected by the Convert.com experiment variation (not in this
// theme) — this file exposes window.dnkPairPopupTracking for manual calls, AND auto-wires
// itself to the popup's actual markup (id="csp-overlay", set by the Convert variation) so no
// calls are needed on the Convert side at all.
//
// Manual usage (optional — the auto-wiring below covers this for the #csp-overlay markup):
//   dnkPairPopupTracking.viewItemList(items, listId, listName)
//   dnkPairPopupTracking.selectItem(item, listId, listName)
//   dnkPairPopupTracking.addToCart(item, listId, listName, value, currency)
//   dnkPairPopupTracking.close(productName, headerText)
//
// listName is 'pair_with_your_sunscreen' or 'pair_with_your_moisturizer'.
// item / items entries: { item_id, item_name, index, item_brand, item_category,
//   item_category2, item_category3, item_category4, item_category5, item_variant, price,
//   discount, quantity } — discount/quantity are only read by addToCart.
//
// Auto-wiring targets the popup structure as built by the Convert variation:
//   #csp-overlay > #csp-banner (header text, e.g. "Pair with your Sunscreen"), #csp-close,
//     #csp-product-title-link > #csp-product-title, #csp-product-image-link,
//     .csp-variant-chip.csp-variant-active[data-variant-id] (selected variant),
//     #csp-offer-price / #csp-original-price, product-form[data-proid] > form > #csp-add-btn.
// view_item_list fires when #csp-overlay transitions to visible (Convert reuses one overlay
// element and toggles a "csp-visible" class rather than inserting a fresh element per open, so
// this is tracked as a hidden->visible state transition, not a one-time DOM-insertion check) —
// but per the tracking spec, the same item_id is only reported once per page load: reopening
// the popup for a product already impressed on this page does not re-fire it. A different
// product (e.g. the popup recommending a different item on reopen) still fires normally, and
// the dedup resets on navigation to a new page since the module state is reinitialized.
// select_item fires on click of the product title/image link, every time, with no suppression.
// add_to_cart fires once the form's submit actually succeeds (see IMPORTANT note below for how
// that's determined), every time, with no suppression. close fires on click of #csp-close.
//
// IMPORTANT — the popup's <form> is missing the data-product-form attribute that
// assets/product-form.js needs to wire up its own AJAX submit handler (compare
// snippets/product-form.liquid, which always sets both data-product-form and
// data-type="add-to-cart-form" — the popup's form only has the latter). Without it, a plain
// submit would do a bare native POST to /cart/add (full page navigation) instead of the AJAX
// flow. Rather than depend on the Convert variation markup being fixed, this file wires its
// own submit handler onto the popup's form as a fallback: it replicates the AJAX add (via
// CartAPI + the cart drawer's renderContents) and fires add_to_cart once that succeeds. If
// data-product-form is ever added on the Convert side, this file detects that and defers to
// the theme's normal product-form.js flow instead, just confirming success via "cart:rendered".
(() => {
  const CLIENT_ID_STORAGE_KEY = "themex-client-id";

  // Resolves to the GA4 client id (from the _ga cookie) when available, otherwise falls
  // back to a self-generated id persisted in localStorage so a value is always sent.
  const getClientId = () => {
    const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (gaCookie) {
      const parts = decodeURIComponent(gaCookie[1]).split(".");
      if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
    }

    let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
    if (!clientId) {
      clientId = crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
    }
    return clientId;
  };

  // window.dnkExperimentVariant is the hook a CRO/experiment platform can set at runtime.
  const getExpVariantString = () => window.dnkExperimentVariant || null;

  const normalizeItem = (item, listId, listName) => ({
    item_id: item.item_id,
    item_name: item.item_name,
    index: item.index,
    item_brand: item.item_brand || "NA",
    item_category: item.item_category || "NA",
    item_category2: item.item_category2 || "NA",
    item_category3: item.item_category3 || "NA",
    item_category4: item.item_category4 || "NA",
    item_category5: item.item_category5 || "NA",
    item_list_id: listId,
    item_list_name: listName,
    item_variant: item.item_variant || null,
    price: item.price,
  });

  const pushEcommerceEvent = (eventName, listId, listName, ecommerce) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      client_id: getClientId(),
      exp_variant_string: getExpVariantString(),
      item_list_id: listId,
      item_list_name: listName,
      ecommerce,
    });
  };

  const viewItemList = (items, listId, listName) => {
    const validItems = (Array.isArray(items) ? items : []).filter((item) => item?.item_id);
    if (!validItems.length) return;

    pushEcommerceEvent("view_item_list", listId, listName, {
      items: validItems.map((item) => normalizeItem(item, listId, listName)),
    });
  };

  const selectItem = (item, listId, listName) => {
    if (!item?.item_id) return;

    pushEcommerceEvent("select_item", listId, listName, {
      items: [normalizeItem(item, listId, listName)],
    });
  };

  // Call only after the popup's add-to-cart request has succeeded.
  const addToCart = (item, listId, listName, value, currency = "INR") => {
    if (!item?.item_id) return;

    pushEcommerceEvent("add_to_cart", listId, listName, {
      currency,
      value,
      items: [
        {
          ...normalizeItem(item, listId, listName),
          discount: item.discount || "0",
          quantity: item.quantity || 1,
        },
      ],
    });
  };

  // No ecommerce payload for this event per the tracking spec.
  const close = (productName, headerText) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "product_pair_popup_close",
      product_name: productName,
      header_text: headerText,
      client_id: getClientId(),
    });
  };

  window.dnkPairPopupTracking = { viewItemList, selectItem, addToCart, close };

  // --- Auto-wiring: fires the above automatically from the popup's real (#csp-*) markup ---

  const deriveListName = (bannerText) => {
    const text = (bannerText || "").toLowerCase();
    if (text.includes("moistur")) return "pair_with_your_moisturizer";
    if (text.includes("sunscreen")) return "pair_with_your_sunscreen";
    return null;
  };

  const parsePrice = (text) => {
    const match = (text || "").replace(/,/g, "").match(/[\d.]+/);
    return match ? Number(match[0]) : undefined;
  };

  const readPopupItem = (overlayEl) => {
    // Multiple simultaneously-active chips are possible (e.g. separate color + size selector
    // groups) — the tracking spec requires all selected values pipe-joined into item_variant
    // ("Orange | 50gm"), so this reads every active chip rather than just the first one.
    const activeVariants = [...overlayEl.querySelectorAll(".csp-variant-chip.csp-variant-active")];
    const variantWithId = activeVariants.find((chip) => chip.dataset.variantId);
    const productForm = overlayEl.querySelector("product-form");
    const offerPrice = parsePrice(overlayEl.querySelector("#csp-offer-price")?.textContent);
    const originalPrice = parsePrice(overlayEl.querySelector("#csp-original-price")?.textContent);

    return {
      item_id: variantWithId?.dataset.variantId || productForm?.dataset.proid,
      item_name: overlayEl.querySelector("#csp-product-title")?.textContent?.trim(),
      index: 1,
      item_brand: "Dot & Key",
      item_variant: activeVariants.length
        ? activeVariants.map((chip) => chip.textContent?.trim()).filter(Boolean).join(" | ")
        : null,
      price: offerPrice,
      discount: originalPrice && offerPrice ? String(originalPrice - offerPrice) : "0",
      quantity: 1,
    };
  };

  // Fires add_to_cart for a pre-captured item/list snapshot. The snapshot is read at submit
  // time (see wireAddToCartForm below), not after the add completes — Convert's popup can
  // change or clear its own content once a successful add happens (it already toggles content
  // on open/close via the same overlay element), so re-reading the DOM after the async request
  // risks reading a torn-down popup and silently dropping the event. Reading at submit time
  // also guarantees the reported item matches what was actually sent to /cart/add.js.
  const trackAddToCart = (listName, item) => {
    if (!listName || !item.item_id || !Number.isFinite(item.price)) return;
    addToCart(item, listName, listName, (item.price * item.quantity).toFixed(2));
  };

  // Wires the popup's <form> so add_to_cart can be tracked, and — see IMPORTANT note above —
  // works around the missing data-product-form attribute so the popup's own submit actually
  // works via the theme's AJAX cart instead of reloading the page.
  const wireAddToCartForm = (overlayEl) => {
    const form = overlayEl.querySelector("form");
    if (!form || form.dataset.pairPopupAtcWired) return;
    form.dataset.pairPopupAtcWired = "true";

    if (form.hasAttribute("data-product-form")) {
      // product-form.js already owns the AJAX submit — just confirm success via the cart
      // drawer's re-render event before tracking.
      form.addEventListener("submit", () => {
        const listName = deriveListName(overlayEl.querySelector("#csp-banner")?.textContent);
        const item = readPopupItem(overlayEl);
        document.addEventListener("cart:rendered", () => trackAddToCart(listName, item), { once: true });
      });
      return;
    }

    form.addEventListener("submit", async (event) => {
      event.preventDefault();

      const listName = deriveListName(overlayEl.querySelector("#csp-banner")?.textContent);
      const item = readPopupItem(overlayEl);

      const cartDrawer = document.querySelector("cart-drawer");
      cartDrawer?.openCart();

      const formData = new FormData(form);
      const sectionId = cartDrawer?.dataset?.sectionId ?? "cart-drawer";
      formData.set("sections", sectionId);
      formData.set("sections_url", window.location.pathname);
      formData.set("properties[is_app]", "true");

      try {
        const response = await CartAPI.add(formData);
        cartDrawer?.renderContents(response);
        trackAddToCart(listName, item);
      } catch (error) {
        // The popup has no error UI of its own — fail silently rather than breaking the page.
      }
    });
  };

  // Convert reuses a single #csp-overlay element and toggles the "csp-visible" class to
  // show/hide it rather than inserting/removing it from the DOM each time, so this tracks
  // hidden->visible transitions (not just first insertion).
  const visiblePopups = new WeakSet();

  // Per the tracking spec, the same product impression must not re-fire while the user stays
  // on the page — only once navigation happens (a fresh page load re-runs this whole module,
  // resetting the set) can the same item_id be impressed again.
  const impressedItemIds = new Set();

  const handlePopupVisibility = (overlayEl) => {
    const isVisible = overlayEl.classList.contains("csp-visible");
    const wasVisible = visiblePopups.has(overlayEl);

    if (isVisible && !wasVisible) {
      visiblePopups.add(overlayEl);
      wireAddToCartForm(overlayEl);

      const listName = deriveListName(overlayEl.querySelector("#csp-banner")?.textContent);
      const item = readPopupItem(overlayEl);
      if (listName && item.item_id && !impressedItemIds.has(item.item_id)) {
        impressedItemIds.add(item.item_id);
        viewItemList([item], listName, listName);
      }
    } else if (!isVisible && wasVisible) {
      visiblePopups.delete(overlayEl);
    }
  };

  // Watches one overlay element's own class attribute — far cheaper than observing class
  // changes across the whole page, which would otherwise re-run on every unrelated class
  // toggle site-wide (sliders, dropdowns, sticky headers, etc.).
  const observedOverlays = new WeakSet();

  const observeOverlay = (overlayEl) => {
    if (observedOverlays.has(overlayEl)) return;
    observedOverlays.add(overlayEl);

    handlePopupVisibility(overlayEl);
    new MutationObserver(() => handlePopupVisibility(overlayEl)).observe(overlayEl, {
      attributes: true,
      attributeFilter: ["class"],
    });
  };

  // Only needs to watch for the overlay element itself being inserted/removed — a childList
  // mutation is infrequent compared to attribute churn, so this stays cheap even site-wide.
  const scanForOverlay = () => {
    document.querySelectorAll("#csp-overlay").forEach(observeOverlay);
  };

  new MutationObserver(scanForOverlay).observe(document.body, { childList: true, subtree: true });
  scanForOverlay();

  document.addEventListener("click", (event) => {
    const closeEl = event.target.closest("#csp-close");
    if (closeEl) {
      const overlayEl = closeEl.closest("#csp-overlay");
      const headerText = overlayEl?.querySelector("#csp-banner")?.textContent?.trim() || "";
      const productName = overlayEl?.querySelector("#csp-product-title")?.textContent?.trim() || "";
      close(productName, headerText);
      return;
    }

    const productLink = event.target.closest("#csp-product-title-link, #csp-product-image-link");
    if (productLink) {
      const overlayEl = productLink.closest("#csp-overlay");
      if (!overlayEl) return;
      const listName = deriveListName(overlayEl.querySelector("#csp-banner")?.textContent);
      const item = readPopupItem(overlayEl);
      if (!listName || !item.item_id) return;

      selectItem(item, listName, listName);
    }
  });
})();
